import getQuantityBreakPricing from "@crescentParts/Handlers/Custom/CustomQuantityBreakPricingHandler";
import {
    CustomCustomQuantityBreakPricingState,
    PriceBreaks,
} from "@crescentParts/Store/CustomQuantityBreakPricing/CustomQuantityBreakPricingReducer";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { Typography } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomCustomQuantityBreakPricingState) => {
    const billToId = state.context.session.billToId;
    const customerNumber = billToId ? state.data?.billTos?.byId[billToId]?.customerNumber : "";
    return {
        location: getLocation(state),
        customQuanityBreakPricingState: state.customQuantityBreakPricing,
        customerNumber,
        currentUserIsGuest: getCurrentUserIsGuest(state),
    };
};

const mapDispatchToProps = {
    getQuantityBreakPricing,
};

interface OwnProps {
    extendedStyles?: ProductQuantityBreakPricingStyles;
    productInfo?: ProductInfo;
    product?: any;
}

export interface ProductQuantityBreakPricingStyles {
    viewLink?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    table?: DataTableProps;
    tableHead?: DataTableHeadProps;
    breakQtyHeader?: DataTableHeaderProps;
    breakPriceHeader?: DataTableHeaderProps;
    savingsHeader?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    breakQtyCell?: DataTableCellBaseProps;
    breakPriceCell?: DataTableCellBaseProps;
    savingsCell?: DataTableCellBaseProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noQtyBreakPricingMessageContainer?: InjectableCss;
    noQtyBreakPricingMessage?: TypographyPresentationProps;
}

export const productQuantityBreakPricingStyles: ProductQuantityBreakPricingStyles = {
    modal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    breakQtyHeader: { alignX: "center" },
    breakPriceHeader: { alignX: "center" },
    savingsHeader: { alignX: "center" },
    breakQtyCell: { alignX: "center" },
    breakPriceCell: { alignX: "center" },
    savingsCell: { alignX: "center" },
    centeringWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            height: 200px;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noQtyBreakPricingMessageContainer: {
        css: css`
            padding: 20px;
        `,
    },
    noQtyBreakPricingMessage: {
        size: 18,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const AHRIRelatedProductQuantityBreakPricing: FC<Props> = ({
    product,
    productInfo,
    customQuanityBreakPricingState: { fetchingFromAPI, qtyBreakPricingByProductNumber },
    getQuantityBreakPricing,
    customerNumber,
    extendedStyles,
    currentUserIsGuest,
}) => {

    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const toasterContext = useContext(ToasterContext);
    const noQtyBreakPricingAvailableMessage = siteMessage("no_qty_break_pricing_available_message");
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewLinkClickHandler = () => {
        setModalIsOpen(true);
        getBreakPricing();
    };

    const [styles] = React.useState(() => mergeToNew(productQuantityBreakPricingStyles, extendedStyles));
    const breakPricing = qtyBreakPricingByProductNumber[product.productNumber];
    const getBreakPricing = () => {
        if (product) {
            if (!breakPricing) {
                getQuantityBreakPricing({
                    productNumber: product.productNumber,
                    customerNumber,
                    onError: error => {
                        const errorMsg = error.toString().length > 400 ? "Something went wrong!" : error;
                        toasterContext.addToast({ body: translate(errorMsg), messageType: "danger" });
                        modalCloseHandler();
                    },
                });
            }
            setLoading(false);
        }
    };

    if (!product || product.quoteRequired || currentUserIsGuest || !customerNumber || !productInfo?.pricing) {
        return null;
    }

    return (
        <>
            <Link {...styles.viewLink} onClick={viewLinkClickHandler} data-test-selector="quantityBreakPricingLink">
                {translate("View Quantity Break Pricing")}
            </Link>
            <Modal
                {...styles.modal}
                headline={translate("Quantity Pricing")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
                data-test-selector="quantityBreakPricingModal"
            >
                {loading || fetchingFromAPI ? (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                ) : breakPricing?.length > 1 ? (
                    <DataTable {...styles.table}>
                        <DataTableHead {...styles.tableHead}>
                            <DataTableHeader {...styles.breakQtyHeader}>{translate("Min Qty")}</DataTableHeader>
                            <DataTableHeader {...styles.breakPriceHeader}>{translate("Price Per")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody {...styles.tableBody}>
                            {breakPricing?.map((breakPrice: PriceBreaks, i: number) => (
                                <DataTableRow
                                    {...styles.tableRow}
                                    key={breakPrice.from}
                                    data-test-selector="breakPriceRow"
                                >
                                    <DataTableCell {...styles.breakQtyCell} data-test-selector="breakQty">
                                        {breakPrice.from}
                                    </DataTableCell>
                                    <DataTableCell {...styles.breakPriceCell} data-test-selector="price">
                                        {`$${breakPrice.price}`}
                                    </DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                ) : (
                    <StyledWrapper {...styles.noQtyBreakPricingMessageContainer}>
                        <Typography {...styles.noQtyBreakPricingMessage}>
                            {noQtyBreakPricingAvailableMessage}
                        </Typography>
                    </StyledWrapper>
                )}
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AHRIRelatedProductQuantityBreakPricing);
